/* eslint-disable @next/next/no-img-element */
'use client';

import React from 'react';
import { ChevronLeft, Menu } from '@repo/ui/components/ui/icons';
import { Button } from '@repo/ui/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { updateRootVariable } from '@web/utils/cssVariable';

type IGridImageViewerProps = {
  urls: string[];
};

type IBottomSheetProps = {
  visible: boolean;
  children: React.ReactNode;
  onExitComplete: () => void;
};

const BottomSheet = ({
  visible,
  children,
  onExitComplete,
}: IBottomSheetProps) => {
  return (
    <AnimatePresence mode="popLayout" onExitComplete={onExitComplete}>
      {visible ? (
        <motion.div
          id="bottom-sheet"
          initial={{ y: '20%', opacity: 0 }}
          animate={{ y: '0%', opacity: 1 }}
          exit={{ y: '20%', opacity: 0 }}
          transition={{ type: 'spring', duration: 0.4, bounce: 0 }}
          className="fixed bottom-0 left-0 right-0 z-[99999] h-full bg-white shadow-md"
        >
          <div className="h-screen overflow-y-auto">{children}</div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export const GridImageViewer = ({ urls }: IGridImageViewerProps) => {
  const [visible, setVisible] = React.useState(false);

  const onOpenGallery = () => {
    setVisible(true);
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      updateRootVariable('--scrollbar-gutter', 'auto');
    }, 500);
  };

  const _renderImages = () => {
    if (urls.length === 1) {
      return (
        <div className="max-h-[486px] md:grid md:h-[486px] md:max-h-[486px]">
          <div className="max-h-[486px] min-h-[130px] w-full overflow-hidden rounded-[4px] md:row-span-3 md:max-h-[486px]">
            <img
              src={urls[0]}
              alt={urls[0]}
              role="button"
              onClick={onOpenGallery}
              className="block aspect-video h-full max-h-[450px] w-full max-w-full object-cover md:max-h-full"
            />
          </div>
        </div>
      );
    }
    if (urls.length === 2 || urls.length > 3) {
      return (
        <div className="relative max-h-[486px] md:grid md:h-[486px] md:max-h-[486px]">
          <div className="relative max-h-[486px] min-h-[130px] w-full overflow-hidden rounded-[4px] md:row-span-3 md:max-h-[486px]">
            <img
              src={urls[0]}
              alt={urls[0]}
              role="button"
              onClick={onOpenGallery}
              className="block aspect-video h-full max-h-[450px] w-full max-w-full object-cover md:max-h-full"
            />
            <Button
              className="absolute bottom-4 right-4"
              variant={'secondary'}
              onClick={onOpenGallery}
            >
              <Menu />
              Galley
            </Button>
          </div>
        </div>
      );
    }

    if (urls.length === 3) {
      const firstImage = urls[0];
      const secondImage = urls[1];
      const thirdImage = urls[2];
      return (
        <div className="relative max-h-[486px] gap-2 md:grid md:h-[486px] md:max-h-[486px] md:grid-cols-[50%_1fr]">
          <div
            className="relative max-h-[486px] min-h-[130px] w-full overflow-hidden rounded-[4px] md:row-span-2 md:max-h-[486px]"
            role="button"
            onClick={onOpenGallery}
          >
            <img
              src={firstImage}
              alt={firstImage}
              className="block h-full max-h-[450px] w-full max-w-full object-cover md:max-h-full"
            />
          </div>
          <div
            className="hidden max-h-[486px] min-h-[130px] w-full overflow-hidden rounded-[4px] md:block"
            role="button"
            onClick={onOpenGallery}
          >
            <img
              src={secondImage}
              className="block h-full w-full max-w-full object-cover"
            />
          </div>
          <div
            className="hidden max-h-[486px] min-h-[130px] w-full overflow-hidden rounded-[4px] md:block"
            role="button"
            onClick={onOpenGallery}
          >
            <img
              src={thirdImage}
              className="block h-full w-full max-w-full object-cover"
            />
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {_renderImages()}
      <BottomSheet
        visible={visible}
        onExitComplete={() => {
          document.body.style.overflow = 'auto';
        }}
      >
        <div className="flex items-center justify-between p-4">
          <ChevronLeft
            className="sticky top-0 h-6 w-6 text-black"
            role="button"
            onClick={() => {
              setVisible(false);
              updateRootVariable('--scrollbar-gutter', 'stable');
            }}
          />
        </div>
        <div
          className={`${urls.length > 1 ? 'grid-cols-2' : ''} m-auto grid max-w-5xl gap-2 p-4`}
        >
          {urls.map((url, index) => (
            <div key={index} className="relative aspect-video">
              <img
                src={url}
                alt={url}
                className="block h-full w-full object-cover"
              />
            </div>
          ))}
        </div>
      </BottomSheet>
    </>
  );
};
